/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
/**
 *
 * @author Mikhail Kokoulin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.25.1
 * @since 1.0.0
 */
import get from 'lodash/get';
import { createSelector } from 'reselect';
import { OutputSelector } from 'reselect/src';

import { IWidgetSearchAccountsItem } from '@pages/PageDashboard/components/Widgets/WidgetSearchAccounts/types/widgetSearchAccountsTypes';
import { IRootReducer } from '@reducers/rootReducer';

import { AccessComponentKeyEnum } from '../role/constants/guard/guardConstants';
import { initialStorageState, ISessionStorageTimers, IStorageReducerState } from './storageReducer';
import { ECookieKey, ELocalStorageKeys, ICookies } from './storageTypes';

/**
 * To state from the global state of storage
 * @param {IRootReducer} state
 */
export const selectStorage = (state: IRootReducer) => get(state, 'storage', initialStorageState);

/**
 * Getting a list of cookies
 * @returns {OutputSelector<IRootReducer, ICookies, {}>}
 */
export const makeSelectCookies = (): OutputSelector<
    IRootReducer,
    ICookies,
    (state: IStorageReducerState) => ICookies
> => createSelector(selectStorage, (state: IStorageReducerState) => state.cookies);

export const makeSelectIsDensity = (): OutputSelector<
    IRootReducer,
    boolean,
    (state: IStorageReducerState) => boolean
> => createSelector(selectStorage, (state) => state.cookies[ECookieKey.DENSITY_LIST_VIEW]);

export const makeSelectTimers = (): OutputSelector<
    IRootReducer,
    ISessionStorageTimers[],
    (state: IStorageReducerState) => ISessionStorageTimers[]
> =>
    createSelector(selectStorage, (state: IStorageReducerState) =>
        get(state, 'sessionStorage.timers', initialStorageState.sessionStorage.timers),
    );

export const makeSelectLocalStorageSelectedAccount = (): OutputSelector<
    IRootReducer,
    IWidgetSearchAccountsItem,
    (state: IStorageReducerState) => IWidgetSearchAccountsItem
> =>
    createSelector(selectStorage, (state) => {
        const selectedAccount = state.localStorage[ELocalStorageKeys.DASHBOARD_SELECTED_ACCOUNT];
        return {
            ...selectedAccount,
            id: parseInt(selectedAccount.id),
        };
    });

export const makeSelectLocalStorageCurrentUserId = (): OutputSelector<
    IRootReducer,
    number,
    (state: IStorageReducerState) => number
> => createSelector(selectStorage, (state) => state.localStorage[ELocalStorageKeys.CURRENT_USER_ID]);

export const makeSelectLocalStorageSettingsProfileParams = (): OutputSelector<
    IRootReducer,
    any,
    (state: IStorageReducerState) => any
> => createSelector(selectStorage, (state) => state.localStorage[ELocalStorageKeys.SETTINGS_PROFILE_PARAMS]);

export const makeSelectLocalStorageTableBtnActive = (): OutputSelector<
    IRootReducer,
    AccessComponentKeyEnum | null,
    (state: IStorageReducerState) => AccessComponentKeyEnum | null
> => createSelector(selectStorage, (state) => state.localStorage[ELocalStorageKeys.TABLE_BTN_ACTIVE]);

export const makeSelectLocalStorageIsSubscribersInRoamingPlateHidden  = (): OutputSelector<
    IRootReducer,
    boolean,
    (state: IStorageReducerState) => boolean
> => createSelector(
        selectStorage, 
        (state) => state.localStorage[ELocalStorageKeys.IS_SUBSCRIBERS_IN_ROAMING_PLATE_HIDDEN],
    );
